import dayjs from 'dayjs';
import React from 'react';
import styled from 'styled-components';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';

import { transactionType } from '../../../constants/constants';
import useScreenQuery from '../../../hooks/useScreenQuery';
import theme from '../../../themes/playupTheme';
import { formatCurrency } from '../../../utils/formatCurrency';
import { formatDate } from '../../../utils/formatDate';
import { formatTime } from '../../../utils/formatTime';
import PlayUpLogoInverted from '../../../assets/images/playup-logo-inverted.svg';
import DraftstarsLogoInverted from '../../../assets/images/draftstars-logo-inverted.png';
import BookiLogoInverted from '../../../assets/images/booki-logo.png';
import { BrandNames } from './transactionsData';

const TransactionItem = ({ transaction, brandName, region = 'enAU' }: Props) => {
	const { isSmall } = useScreenQuery();
	const [showDetails, setShowDetails] = React.useState(false);

	const renderDesktopTransaction = () => {
		return (
			<>
				<FieldsContainer isSmallScreen={false}>
					<DesktopFieldItemsContainer>
						<NormalText isSmallScreen={false}>#{transaction.id}</NormalText>
						<TransactionTypeLabel isSmallScreen={false} isDebit={transaction.type.id === transactionType.DEBIT}>
							<TransactionTypeLabelText isSmallScreen={false}>{transaction.type.name}</TransactionTypeLabelText>
						</TransactionTypeLabel>
					</DesktopFieldItemsContainer>
					<DesktopFieldItemsContainer>
						<NormalText isSmallScreen={false}>{formatDate(dayjs(transaction.created_at), region)}</NormalText>
						<LightText isSmallScreen={false}>{formatTime(dayjs(transaction.created_at), region)}</LightText>
					</DesktopFieldItemsContainer>
					<DesktopFieldItemsContainer>
						<NormalText isSmallScreen={false}>{transaction.category.name}</NormalText>
					</DesktopFieldItemsContainer>
					<DesktopFieldItemsContainer>
						{transaction?.meta?.bet_ticket && (
							<>
								{transaction.meta.bet_ticket.startsWith('#1') &&
									(brandName === BrandNames.Booki ? (
										<BrandLogo isSmallScreen={false} src={BookiLogoInverted} alt={'Booki'} />
									) : (
										<BrandLogo isSmallScreen={false} src={PlayUpLogoInverted} alt={'PlayUp'} />
									))}
								{transaction.meta.bet_ticket.startsWith('#2') && (
									<BrandLogo isSmallScreen={false} src={DraftstarsLogoInverted} alt={'Draftstars'} />
								)}
							</>
						)}
					</DesktopFieldItemsContainer>
					<DesktopFieldItemsContainer>
						<AmountText isSmallScreen={false} isDebit={transaction.type.id === transactionType.DEBIT}>
							{transaction.type.name === 'Debit' ? '-' : ''}
							{formatCurrency(transaction.amount)}
						</AmountText>
					</DesktopFieldItemsContainer>
					<DesktopFieldItemsContainer>
						<HeavyText isSmallScreen={false}>{formatCurrency(transaction.balance)}</HeavyText>
					</DesktopFieldItemsContainer>
				</FieldsContainer>
				{renderDetailsSection()}
			</>
		);
	};

	const renderMobileTransaction = () => {
		return (
			<FieldsContainer isSmallScreen={true}>
				<MobileFieldItemsContainer>
					<MobileFieldItemLevelContainer>
						<MobileFieldItemLeftContainer>
							<NormalText isSmallScreen={true}>TX ID: #{transaction.id}</NormalText>
							<TransactionTypeLabel isSmallScreen={true} isDebit={transaction.type.id === transactionType.DEBIT}>
								<TransactionTypeLabelText isSmallScreen={true}>{transaction.type.name}</TransactionTypeLabelText>
							</TransactionTypeLabel>
						</MobileFieldItemLeftContainer>
						<MobileFieldItemRightContainer>
							<NormalText isSmallScreen={true}>{formatDate(dayjs(transaction.created_at), region)}</NormalText>
							<LightText isSmallScreen={true}>{formatTime(dayjs(transaction.created_at), region)}</LightText>
						</MobileFieldItemRightContainer>
					</MobileFieldItemLevelContainer>
					<MobileFieldItemLevelContainer>
						<MobileFieldItemLeftContainer>
							<NormalText isSmallScreen={true}>Type: {transaction.category.name}</NormalText>
						</MobileFieldItemLeftContainer>
						<MobileFieldItemRightContainer>
							{transaction?.meta?.bet_ticket && (
								<>
									{transaction.meta.bet_ticket.startsWith('#1') &&
										(brandName === BrandNames.Booki ? (
											<NormalText isSmallScreen={true}>
												<BrandLogo isSmallScreen={true} src={BookiLogoInverted} alt={'Booki'} />
											</NormalText>
										) : (
											<NormalText isSmallScreen={true}>
												<BrandLogo isSmallScreen={true} src={PlayUpLogoInverted} alt={'PlayUp'} />
											</NormalText>
										))}
									{transaction.meta.bet_ticket.startsWith('#2') && (
										<NormalText isSmallScreen={true}>
											<BrandLogo isSmallScreen={true} src={DraftstarsLogoInverted} alt={'Draftstars'} />
										</NormalText>
									)}
								</>
							)}
						</MobileFieldItemRightContainer>
					</MobileFieldItemLevelContainer>
					<MobileAmountContainer>
						<HeavyText isSmallScreen={true}>Amount:</HeavyText>
						<AmountText isSmallScreen={true} isDebit={transaction.type.id === transactionType.DEBIT}>
							{transaction.type.name === 'Debit' ? '-' : ''}
							{formatCurrency(transaction.amount)}
						</AmountText>
					</MobileAmountContainer>
					<MobileAmountContainer>
						<HeavyText isSmallScreen={true}>Balance:</HeavyText>
						<HeavyText isSmallScreen={true}>{formatCurrency(transaction.balance)}</HeavyText>
					</MobileAmountContainer>
				</MobileFieldItemsContainer>
				{renderDetailsSection()}
			</FieldsContainer>
		);
	};

	const getDisplayNameFromMetaKey = (key) => {
		key = key.replace(/_/g, ' ');
		return key.replace(/\w\S*/g, (m) => m.charAt(0).toUpperCase() + m.substr(1).toLowerCase());
	};

	const renderDetailsSection = () => (
		<DetailsSection isSmallScreen={isSmall}>
			<DetailsHeader onClick={() => setShowDetails(!showDetails)}>
				<HeavyText isSmallScreen={isSmall}>Details</HeavyText>
				<DetailsHeaderIconContainer isSmallScreen={isSmall}>
					{showDetails ? <FaChevronUp /> : <FaChevronDown />}
				</DetailsHeaderIconContainer>
			</DetailsHeader>
			{showDetails &&
				transaction.meta &&
				Object.entries(transaction.meta).map(([key, value]) => {
					return (
						<DetailsItemContainer key={key}>
							<HeavyText isSmallScreen={isSmall}>{getDisplayNameFromMetaKey(key)}: </HeavyText>
							<NormalText isSmallScreen={isSmall}>{value}</NormalText>
						</DetailsItemContainer>
					);
				})}
		</DetailsSection>
	);
	return (
		<TransactionContainer isSmallScreen={isSmall}>
			{isSmall ? renderMobileTransaction() : renderDesktopTransaction()}
		</TransactionContainer>
	);
};

export default TransactionItem;

interface Props {
	transaction: WalletTransaction;
	region?: string;
	brandName: string;
}

const TransactionContainer = styled.div<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	display: 'flex',
	flexDirection: 'column',
	margin: isSmallScreen ? '5px 0' : '10px 0',
	backgroundColor: theme.colors.white,
	borderRadius: '5px',
}));

const FieldsContainer = styled.div<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	display: 'flex',
	flexDirection: isSmallScreen ? 'column' : 'row',
}));

const BrandLogo = styled.img<{ isSmallScreen: boolean }>(({ isSmallScreen }) => ({
	height: isSmallScreen ? '25px' : '30px',
}));

const DesktopFieldItemsContainer = styled.div({
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'flex-start',
	padding: '12px 18px',
});

const NormalText = styled.p<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	color: theme.colors.darkR,
	fontSize: isSmallScreen ? '14px' : '18px',
	fontWeight: 'medium',
}));

const LightText = styled.p<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	color: theme.colors.greyR,
	fontSize: isSmallScreen ? '14px' : '16px',
}));

const HeavyText = styled.p<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	color: theme.colors.darkR,
	fontSize: isSmallScreen ? '14px' : '18px',
	fontWeight: 'bold',
}));

const AmountText = styled(HeavyText)<{ isDebit: boolean }>(({ theme, isDebit }) => ({
	color: isDebit ? theme.colors.danger : theme.colors.success,
}));

const TransactionTypeLabel = styled.div<{ isSmallScreen: boolean; isDebit: boolean }>(
	({ theme, isSmallScreen, isDebit }) => ({
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: isSmallScreen ? '75px' : '95px',
		height: '30px',
		margin: '5px 0 ',
		backgroundColor: isDebit ? theme.colors.danger : theme.colors.success,
		borderRadius: '5px',
	})
);

const TransactionTypeLabelText = styled.p<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	color: theme.colors.white,
	fontSize: isSmallScreen ? '14px' : '16px',
}));

const MobileFieldItemsContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	padding: '10px 15px 0',
});

const MobileFieldItemLevelContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
});
const MobileFieldItemLeftContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flexBasis: '50%',
	alignItems: 'flex-start',
});

const MobileFieldItemRightContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flexBasis: '50%',
	alignItems: 'flex-end',
});

const MobileAmountContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	marginTop: '8px',
	paddingTop: '8px',
	borderTop: '1px solid',
	borderTopColor: theme.colors.lightGreyR,
});

const DetailsSection = styled.div<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	display: 'flex',
	flexDirection: 'column',
	margin: isSmallScreen ? '8px 15px' : '15px 18px',
	borderTop: '1px solid',
	borderTopColor: theme.colors.lightGreyR,
}));

const DetailsHeader = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	cursor: 'pointer',
	paddingTop: '8px',
	paddingBottom: '10px',
});

const DetailsHeaderIconContainer = styled.div<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	fontSize: isSmallScreen ? '14px' : '18px',
	color: theme.colors.darkR,
}));

const DetailsItemContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	marginBottom: '10px',
});
